import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Clock3, Sparkles, Trophy, XCircle } from 'lucide-react';
import { Reveal, shortDate, relativeTime } from './npUi';

const STAGE_ICONS = ['1', '2', '3', '4', '5', '$'];

export function TrackerStages({ stages = [], status = 'active', compact = false }) {
  return (
    <div className={`np-stages ${compact ? 'compact' : ''}`} data-testid="tracker-stages">
      {stages.map((stage, index) => {
        const won = status === 'won' && stage.id === 'money_back';
        const cls = ['np-stage'];
        if (stage.done) cls.push('done');
        if (stage.current) cls.push('current');
        if (won) cls.push('won');
        if (stage.skipped) cls.push('skipped');
        return (
          <motion.div
            key={stage.id}
            className={cls.join(' ')}
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.06, duration: 0.4 }}
          >
            <div className="np-stage-dot" aria-hidden="true">
              {stage.done || won ? <Check size={14} strokeWidth={3} /> : stage.current ? <Clock3 size={14} /> : STAGE_ICONS[index]}
            </div>
            <div className="np-stage-label">{stage.label}</div>
            {!compact ? <div className="np-stage-when">{stage.at ? shortDate(stage.at) : stage.current ? 'now' : ''}</div> : null}
          </motion.div>
        );
      })}
    </div>
  );
}

export function TrackerCard({ item, onOpen, showOpen = true }) {
  const tone = item.status === 'won' ? 'green' : item.status === 'closed' ? 'rose' : item.needs_you ? 'amber' : 'blue';
  const tag = item.status === 'won'
    ? (item.outcome === 'AVOIDED' ? 'Charge avoided' : 'Money back')
    : item.status === 'closed'
      ? 'Closed'
      : item.needs_you ? 'Needs your OK' : item.stage_label;
  return (
    <motion.div
      layout
      className={`np-tracker-card ${item.status}`}
      data-testid={`tracker-card-${item.case_id}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98 }}
    >
      <div className="np-tracker-top">
        <div>
          <div className="np-row">
            <span className={`np-tag ${tone}`}>
              {item.status === 'won' ? <Trophy size={12} /> : item.status === 'closed' ? <XCircle size={12} /> : <Sparkles size={12} />}
              {tag}
            </span>
            <span className="np-muted">{item.workflow_label}</span>
          </div>
          <h3>{item.title}</h3>
          <p className="np-muted" style={{ margin: 0 }}>
            {item.merchant ? `${item.merchant} · ` : ''}
            {item.status === 'active' ? `open ${item.days_open} day${item.days_open === 1 ? '' : 's'}` : `updated ${relativeTime(item.updated_at)}`}
            {item.days_left !== null && item.days_left !== undefined && item.status === 'active' ? ` · ${item.days_left} day${item.days_left === 1 ? '' : 's'} left` : ''}
          </p>
        </div>
        <div>
          <div className={`np-tracker-amount ${item.status === 'won' ? 'np-money' : ''}`}>{item.amount_display}</div>
          <div className="np-muted" style={{ textAlign: 'right', fontSize: 12 }}>{item.progress_pct}% there</div>
        </div>
      </div>
      <TrackerStages stages={item.stages} status={item.status} />
      <div className="np-tracker-next">
        <span className="np-muted">Next:</span>
        <strong>{item.next_step}</strong>
        {showOpen && onOpen ? (
          <button type="button" className="np-btn ghost np-btn-sm np-btn-icon" style={{ marginLeft: 'auto' }} onClick={() => onOpen(item.case_id)} data-testid="tracker-open-case">
            {item.needs_you ? 'Review now' : 'Open'} <ArrowRight size={14} />
          </button>
        ) : null}
      </div>
    </motion.div>
  );
}

export default function RecoveryTracker({ data, loading = false, embedded = false }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('active');
  const items = data?.items || [];
  const shown = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'needs_you') return items.filter((i) => i.status === 'active' && i.needs_you);
    return items.filter((i) => i.status === filter);
  }, [items, filter]);
  const counts = data?.counts || {};
  const byStage = counts.by_stage || {};

  if (loading && !data) {
    return (
      <div className="np-tracker">
        {[0, 1].map((i) => <div key={i} className="np-skeleton" style={{ height: 180 }} />)}
      </div>
    );
  }

  return (
    <div className="np-tracker" data-testid="recovery-tracker">
      {!embedded ? (
        <Reveal className="np-card">
          <div className="np-between">
            <div>
              <h3 style={{ margin: 0 }}>Where every request stands</h3>
              <p className="np-muted" style={{ margin: '4px 0 0' }}>
                From “found” to “money back in bank”. In flight: <strong style={{ color: '#e8efe6' }}>{data?.in_flight_display || '$0.00'}</strong> · Won: <strong className="np-money">{data?.won_display || '$0.00'}</strong>
              </p>
            </div>
            <div className="np-filter-row">
              {[
                ['active', `In progress (${counts.active || 0})`],
                ['needs_you', `Needs you (${counts.needs_you || 0})`],
                ['won', `Won (${counts.won || 0})`],
                ['closed', `Closed (${counts.closed || 0})`],
                ['all', 'All'],
              ].map(([id, label]) => (
                <button key={id} type="button" className={`np-chip ${filter === id ? 'on' : ''}`} onClick={() => setFilter(id)} data-testid={`tracker-filter-${id}`}>{label}</button>
              ))}
            </div>
          </div>
          <div className="np-tracker-summary" style={{ marginTop: 16 }}>
            {(data?.stages || []).map((stage) => (
              <div key={stage.id}>
                <strong>{stage.id === 'money_back' ? counts.won || 0 : byStage[stage.id] || 0}</strong>
                <span>{stage.label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      ) : null}
      {shown.length === 0 ? (
        <div className="np-empty">
          <strong>{filter === 'active' ? 'Nothing in flight right now' : 'Nothing here yet'}</strong>
          {filter === 'won' ? 'Money shows here once a bank line confirms it.' : 'Connect Gmail or paste a company email to open your first recovery.'}
        </div>
      ) : shown.map((item) => (
        <TrackerCard key={item.case_id} item={item} onOpen={(id) => navigate(`/cases/${id}`)} />
      ))}
    </div>
  );
}
